import { AnalyticsEventLocation } from '@mb/lib';
import { useSeeAllData } from '../hooks/useSeeAllData';

interface IAnalyticsEvent {
  event: EventName;
  type: EventType | null;
  action: EventAction | CarouselAction | null;
  name: string | null;
  method: string | null;
  experiment_name: string | null;
  experiment_version: string | null;
  event_location: AnalyticsEventLocation | null;
  list_id: string | null;
  list_name: string | null;
  status?: string | null;
  filter?: IFilterEvent | null;
  content_id?: string | null;
  content_type?: string | null;
  ecommerce?: any | null;
}

export enum EventName {
  FILTER = 'filter',
  CHANGE_VIEW = 'change_view',
  SELECT_CONTENT = 'select_content',
  VIEW_PROMOTION = 'view_promotion',
  SELECT_PROMOTION = 'select_promotion',
  CAROUSEL = 'carousel',
}

interface IFilterEvent {
  action: FilterAction;
  type: FilterType;
  attribute: string | null;
  name: string;
  results: number;
}

enum FilterAction {
  Apply = 'Apply',
  Remove = 'Remove',
}

enum FilterType {
  Category = 'Category',
}

export enum EventType {
  Layout = 'Layout',
}

export enum EventAction {
  DefaultView = 'Default view',
  ListView = 'List view',
}

// need to wait when window.dataLayer will be initialized and have gtm.load event
const checkDataLayer = (): Promise<void> => {
  return new Promise((resolve) => {
    const check = () => {
      if (
        window.dataLayer &&
        window.dataLayer.find((event: any) => event.event === 'gtm.load')
      ) {
        resolve();
      } else {
        setTimeout(check, 100);
      }
    };
    check();
  });
};

export const sendAnalyticsEvent = async (params: IAnalyticsEvent) => {
  await checkDataLayer();
  // console.log(params);
  window.dataLayer.push(params);
};

export const clearAnalyticsEvents = async (params: any) => {
  await checkDataLayer();
  window.dataLayer.push(params);
};

export const sendSeeAllAnalyticsEvent = (title: string) => {
  const { contentId, contentType } = useSeeAllData(title);

  sendAnalyticsEvent({
    event: EventName.SELECT_CONTENT,
    content_id: contentId,
    content_type: contentType,
    name: `${title} collections`,
    experiment_name: null,
    experiment_version: null,
    event_location: AnalyticsEventLocation.COLLECTIONS_LIBRARY,
    method: null,
    action: null,
    list_id: null,
    list_name: null,
    type: null,
  });
};

interface CategoryEvent {
  selectedCategoryId: string | null;
  name: string;
  results: number;
}

export const sendCategoryChangeAnalyticsEvent = (params: CategoryEvent) => {
  const { selectedCategoryId, name, results } = params;
  const filterAction =
    name === selectedCategoryId ? FilterAction.Remove : FilterAction.Apply;
  clearAnalyticsEvents({
    filter: null,
  }).then(() => {
    sendAnalyticsEvent({
      event: EventName.FILTER,
      filter: {
        action: filterAction,
        type: FilterType.Category,
        attribute: null,
        name: name,
        results: results,
      },
      type: null,
      action: null,
      name: null,
      method: null,
      experiment_name: null,
      experiment_version: null,
      event_location: AnalyticsEventLocation.COLLECTIONS_LIBRARY,
      list_id: null,
      list_name: null,
      status: null,
    });
  });
};

export const sendChangeViewAnalyticsEvent = (isListView: boolean) => {
  sendAnalyticsEvent({
    event: EventName.CHANGE_VIEW,
    type: EventType.Layout,
    action: isListView ? EventAction.ListView : EventAction.DefaultView,
    name: null,
    method: null,
    experiment_name: null,
    experiment_version: null,
    event_location: AnalyticsEventLocation.COLLECTIONS_LIBRARY,
    list_id: null,
    list_name: null,
    status: null,
  });
};

export interface CollectionItemAnalyticsData {
  name: string;
  index: number;
}

interface ViewPromotionAnalyticsEvent {
  collectionName: string;
  items: CollectionItemAnalyticsData[];
}

export const sendViewPromotionAnalyticsEvent = ({
  collectionName,
  items,
}: ViewPromotionAnalyticsEvent) => {
  const promotionId = `${collectionName
    .split(' ')
    .join('_')
    .toLowerCase()}_collections`;
  const promotionName = `${collectionName} collections`;

  sendAnalyticsEvent({
    event: EventName.VIEW_PROMOTION,
    event_location: AnalyticsEventLocation.COLLECTIONS_LIBRARY,
    list_id: promotionId,
    list_name: promotionName,
    experiment_name: null,
    experiment_version: null,
    method: null,
    action: null,
    type: null,
    name: null,
    status: null,
    ecommerce: {
      promotion_id: promotionId,
      promotion_name: promotionName,
      items: items.map((item) => ({
        promotion_id: promotionId,
        promotion_name: promotionName,
        creative_name: item.name,
        creative_slot: `${promotionId}_${item.index + 1}`,
      })),
    },
  });
};

export const sendSelectPromotionAnalyticsEvent = (
  collectionName: string,
  item: CollectionItemAnalyticsData,
) => {
  const promotionId = `${collectionName
    .split(' ')
    .join('_')
    .toLowerCase()}_collections`;
  const promotionName = `${collectionName} collections`;

  sendAnalyticsEvent({
    event: EventName.SELECT_PROMOTION,
    event_location: AnalyticsEventLocation.COLLECTIONS_LIBRARY,
    list_id: promotionId,
    list_name: promotionName,
    experiment_name: null,
    experiment_version: null,
    method: null,
    action: null,
    type: null,
    name: null,
    status: null,
    ecommerce: {
      promotion_id: promotionId,
      promotion_name: promotionName,
      items: [
        {
          promotion_id: promotionId,
          promotion_name: promotionName,
          creative_name: item.name,
          creative_slot: `${promotionId}_${item.index + 1}`,
        },
      ],
    },
  });
};

export enum CarouselAction {
  FORWARD = 'Forward',
  BACK = 'Back',
}

export const sendCarouselAnalyticsEvent = (
  title: string,
  action: CarouselAction,
) => {
  sendAnalyticsEvent({
    event: EventName.CAROUSEL,
    action: action,
    name: `${title} collections`,
    experiment_name: null,
    experiment_version: null,
    event_location: null,
    method: null,
    list_id: null,
    list_name: null,
    type: null,
    status: null,
  });
};
